import React from 'react';
import { useOverlayClose } from './useOverlayClose';

/**
 * The dark-overlay invoice preview shared by every bill create/edit screen.
 *
 * The `draft` flag is the important part: a bill that has not been created yet
 * only *previews* its invoice number (the server assigns the real one on save),
 * so downloading a PDF at that point puts a document in circulation carrying a
 * number that will later be handed to a different bill. In draft mode there is
 * therefore no download button at all — the only way forward is to create the
 * bill, after which the saved bill (with its real number) can be downloaded.
 */
interface Props {
  onClose: () => void;
  /** Bill not saved yet → download is replaced by the create action. */
  draft?: boolean;
  /** Overrides the default amber notice text shown in draft mode. */
  draftNotice?: string;
  /** Label for the draft-mode primary button, e.g. "Create Bill & Download". */
  createLabel?: string;
  /** The screen's save handler; runs from the draft preview. */
  onCreate?: () => void;
  creating?: boolean;
  /** Download handler — used only when `draft` is false. */
  onDownload?: () => void;
  downloading?: boolean;
  /** The invoice document; the caller attaches its own ref for PDF capture. */
  children: React.ReactNode;
}

const DEFAULT_NOTICE =
  'This bill has not been created yet. The invoice number shown is provisional — create the bill to lock it in, then download.';

const overlay: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
  backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 3000, overflowY: 'auto',
  display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px',
};

const baseBtn: React.CSSProperties = {
  padding: '0.6rem 1.5rem', border: 'none', borderRadius: '8px',
  cursor: 'pointer', fontWeight: 600,
};

const InvoicePreviewModal: React.FC<Props> = ({
  onClose, draft = false, draftNotice, createLabel = 'Create Bill & Download',
  onCreate, creating = false, onDownload, downloading = false, children,
}) => {
  const overlayClose = useOverlayClose(onClose);

  return (
    <div style={overlay} {...overlayClose}>
      {draft && (
        <div
          style={{
            maxWidth: 794, marginBottom: '12px', padding: '0.7rem 1rem',
            background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px',
            color: '#92400e', fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.5,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          ⚠ {draftNotice || DEFAULT_NOTICE}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }} onClick={(e) => e.stopPropagation()}>
        {draft ? (
          <button
            style={{ ...baseBtn, backgroundColor: '#16a34a', color: 'white', opacity: creating ? 0.7 : 1, cursor: creating ? 'not-allowed' : 'pointer' }}
            onClick={onCreate}
            disabled={creating}
          >
            {creating ? 'Creating…' : `✅ ${createLabel}`}
          </button>
        ) : (
          <button
            style={{ ...baseBtn, backgroundColor: '#16a34a', color: 'white', opacity: downloading ? 0.7 : 1, cursor: downloading ? 'not-allowed' : 'pointer' }}
            onClick={onDownload}
            disabled={downloading}
          >
            {downloading ? 'Generating PDF...' : '⬇ Download PDF'}
          </button>
        )}
        <button
          style={{ ...baseBtn, backgroundColor: 'white', color: '#334155' }}
          onClick={onClose}
        >
          ✕ Close
        </button>
      </div>

      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
};

export default InvoicePreviewModal;
